/**
 * Builds an HTML string that can be rendered in an iframe for React preview
 * Uses Babel standalone to compile JSX at runtime
 */
export const buildReactPreviewHTML = (files) => {
  const appCode = files['App.jsx'] || '';
  const indexCode = files['index.jsx'] || '';
  const indexHtml = files['index.html'] || '<html><body><div id="root"></div></body></html>';

  // Extract body content from the provided index.html
  const bodyMatch = indexHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const bodyContent = bodyMatch ? bodyMatch[1] : '<div id="root"></div>';

  // Remove import/export statements from app code (they'll be handled globally)
  const appCodeWithoutImports = appCode
    .split('\n')
    .filter(line => !line.trim().startsWith('import ') && !line.trim().startsWith('export '))
    .join('\n')
    .trim();

  // Remove import/export statements from index code (they'll be handled globally)
  const indexCodeWithoutImports = indexCode
    .split('\n')
    .filter(line => !line.trim().startsWith('import ') && !line.trim().startsWith('export '))
    .join('\n')
    .trim();

  // Build the complete HTML with all scripts
  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>React Preview</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
        'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
        sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      background: #fff;
      color: #333;
    }
    #root {
      width: 100%;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
    }
    .error-container {
      padding: 20px;
      background-color: #fee;
      color: #c00;
      font-family: 'Courier New', monospace;
      white-space: pre-wrap;
      word-wrap: break-word;
      border: 1px solid #fcc;
      margin: 10px;
      border-radius: 4px;
    }
    /* Syntax Highlighting - Prism-like colors */
    .code-syntax {
      font-family: 'Courier New', 'Monaco', monospace;
      font-size: 14px;
      line-height: 1.5;
      background: #f5f5f5;
      padding: 12px;
      border-radius: 4px;
      overflow-x: auto;
    }
    .keyword { color: #d73a49; font-weight: bold; }
    .string { color: #032f62; }
    .function { color: #6f42c1; }
    .tag { color: #22863a; }
    .attr { color: #6f42c1; }
    .comment { color: #6a737d; font-style: italic; }
  </style>
</head>
<body>
  ${bodyContent}

  <script crossorigin src="https://cdn.jsdelivr.net/npm/react@18/umd/react.production.min.js"><\/script>
  <script crossorigin src="https://cdn.jsdelivr.net/npm/react-dom@18/umd/react-dom.production.min.js"><\/script>
  <script src="https://cdn.jsdelivr.net/npm/@babel/standalone/babel.min.js"><\/script>

  <script>
    const root = document.getElementById("root");

    function showError(title, message, stack = "") {
      root.innerHTML = \`
        <div style="
          padding:20px;
          font-family: monospace;
          background:#fff1f2;
          color:#991b1b;
          border:1px solid #fecaca;
        ">
          <strong>\${title}</strong>
          <pre style="margin-top:10px">\${message}</pre>
          <pre style="font-size:12px;opacity:0.8">\${stack}</pre>
        </div>
      \`;
    }

    try {
      const compiledApp = Babel.transform(
        \`${appCodeWithoutImports}\`,
        { presets: ["react"] }
      ).code;

      const compiledIndex = Babel.transform(
        \`${indexCodeWithoutImports}\`,
        { presets: ["react"] }
      ).code;

      eval(compiledApp);
      eval(compiledIndex);

    } catch (err) {
      showError(
        "❌ Build failed",
        err.message,
        err.stack?.split("\\n").slice(0, 4).join("\\n")
      );
    }
  </script>
</body>
</html>
  `.trim();
};

/**
 * Builds an HTML string for Angular preview (with template extraction and rendering)
 */
function buildAngularErrorHTML(title, message) {
  return `
<!DOCTYPE html>
<html>
<body>
  <div style="
    padding:20px;
    font-family: monospace;
    background:#fff1f2;
    color:#991b1b;
    border:1px solid #fecaca;
  ">
    <strong>❌ ${title}</strong>
    <pre style="margin-top:10px">${message}</pre>
  </div>
</body>
</html>
`.trim();
}

export const buildAngularPreviewHTML = (files) => {
  const appCode = files['app.component.ts'] || '';

  // ❌ No component decorator
  if (!/@Component\s*\(/.test(appCode)) {
    return buildAngularErrorHTML(
      'Invalid Angular Component',
      'Missing @Component decorator.'
    );
  }

  // ❌ No template
  if (!/template\s*:\s*`/.test(appCode)) {
    return buildAngularErrorHTML(
      'Template Error',
      'Missing template property in @Component.'
    );
  }

  // ❌ Unclosed template backtick
  const backtickCount = (appCode.match(/`/g) || []).length;
  if (backtickCount % 2 !== 0) {
    return buildAngularErrorHTML(
      'Template Syntax Error',
      'Unclosed template string (`).'
    );
  }

  let templateContent;
  let className;
  let properties;

  try {
    // Extract template from @Component decorator
    const templateRegex = /template\s*:\s*`([^`]*)`/s;
    const templateMatch = appCode.match(templateRegex);
    templateContent = templateMatch ? templateMatch[1] : '<p>No template found</p>';

    // ❌ Check for unclosed interpolation syntax
    if (templateContent.includes('{{') && !templateContent.includes('}}')) {
      return buildAngularErrorHTML(
        'Template Interpolation Error',
        'Unclosed {{ }} interpolation detected.'
      );
    }

    // Extract component class name
    const classMatch = appCode.match(/export\s+class\s+(\w+)/);
    className = classMatch ? classMatch[1] : 'AppComponent';

    // Extract simple properties for interpolation
    properties = {};
    const propRegex = /(\w+)\s*=\s*['"]([^'"]*)['"]/g;
    let match;
    while ((match = propRegex.exec(appCode)) !== null) {
      properties[match[1]] = match[2];
    }
  } catch (err) {
    return buildAngularErrorHTML(
      'Angular Parse Error',
      err.message
    );
  }

  // Replace template interpolations with actual values
  let renderedTemplate = templateContent;
  Object.entries(properties).forEach(([key, value]) => {
    const interpolationRegex = new RegExp('{{\\s*' + key + '\\s*}}', 'g');
    renderedTemplate = renderedTemplate.replace(interpolationRegex, escapeHtml(value));
  });

  const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Angular Preview</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
        'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
        sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      background: #fff;
      color: #333;
    }
    app-root {
      display: block;
      width: 100%;
      min-height: 100vh;
    }
    .preview-header {
      background-color: #e3f2fd;
      border-bottom: 2px solid #2196f3;
      padding: 12px 20px;
      color: #1565c0;
      font-size: 12px;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    .preview-content {
      padding: 0;
    }
    .error-container {
      padding: 20px;
      background-color: #fee;
      color: #c00;
      font-family: 'Courier New', monospace;
      white-space: pre-wrap;
      word-wrap: break-word;
      border: 1px solid #fcc;
      margin: 10px;
      border-radius: 4px;
    }
  </style>
</head>
<body>
  <div class="preview-header">
    <strong>📦 Angular Component:</strong> ${escapeHtml(className)}
  </div>
  <app-root>
    <div class="preview-content">
      ${renderedTemplate}
    </div>
  </app-root>

  <script>
    try {
      console.log('✅ Angular Preview Loaded - Component: ${className}');

      // Log any properties that were bound
      const props = ${JSON.stringify(properties)};
      if (Object.keys(props).length > 0) {
        console.log('📝 Component Properties:', props);
      }
    } catch (error) {
      console.error('Angular Preview Error:', error);
    }
  </script>
</body>
</html>`;

  return htmlContent.trim();
};

/**
 * Simple HTML escape function
 */
function escapeHtml(text) {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
}
